import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Loader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plug, CreditCard, MessageSquare, Mail, ShieldCheck, Truck, Wallet, CheckCircle2, XCircle, Clock } from "lucide-react";

const META = {
  payment_gateway: { label: "Payment Gateway", icon: CreditCard, detail: "Stripe (test mode) — dynamic INR checkout with verified webhook." },
  sms_otp: { label: "SMS OTP", icon: MessageSquare, detail: "No provider keys configured. DEV/TEST OTP mode active. Add MSG91/Twilio to go live." },
  email: { label: "Email", icon: Mail, detail: "Transactional email via Emergent Resend." },
  gst_verification: { label: "GST Verification", icon: ShieldCheck, detail: "Admin manual review. Automated GST API can be added later." },
  shipping: { label: "Shipping", icon: Truck, detail: "Manual courier entry by manufacturer. Shiprocket/Delhivery later." },
  payouts: { label: "Payouts / Settlements", icon: Wallet, detail: "Recorded manually by admin. Automatic split settlements later." },
  whatsapp: { label: "WhatsApp", icon: MessageSquare, detail: "Ordering & catalogue via WhatsApp." },
};

function statusPill(v) {
  if (["connected", "automated", "integrated"].includes(v)) return { cls: "bg-psage text-forest", icon: CheckCircle2, text: v };
  if (v === "coming_later") return { cls: "bg-sgrey text-ink/60", icon: Clock, text: "COMING LATER" };
  if (v === "manual") return { cls: "bg-amber-100 text-amber-800", icon: Clock, text: "MANUAL" };
  return { cls: "bg-red-100 text-red-700", icon: XCircle, text: v.replace(/_/g, " ") };
}

export default function Integrations() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/settings").then((r) => setS(r.data)).catch(() => setS({}));
  useEffect(() => { load(); }, []);
  if (!s) return <Loader />;
  const integ = s.integrations || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2"><Plug className="h-5 w-5 text-forest" /><h1 className="font-heading text-2xl font-semibold text-forest">Integrations</h1></div>
      <p className="text-sm text-ink/60">Each integration is modular. Statuses reflect what is actually connected — nothing is faked.</p>
      <div className="grid gap-4 lg:grid-cols-2">
        {Object.entries(META).map(([key, m]) => {
          const p = statusPill(integ[key] || "not_connected");
          return (
            <div key={key} data-testid={`integration-${key}`} className="flex items-start justify-between rounded-xl border border-border bg-card p-5">
              <div className="flex gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-psage/50"><m.icon className="h-5 w-5 text-forest" /></div>
                <div>
                  <p className="font-medium text-ink">{m.label}</p>
                  <p className="mt-0.5 max-w-sm text-xs text-ink/60">{m.detail}</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${p.cls}`}><p.icon className="h-3 w-3" /> {p.text}</span>
            </div>
          );
        })}
      </div>

      <LegalConfig legal={s.legal} finance={s.finance} onSaved={load} />
    </div>
  );
}

function LegalConfig({ legal, finance, onSaved }) {
  const [l, setL] = useState(legal || {});
  const [f, setF] = useState(finance || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setL(legal || {}); setF(finance || {}); }, [legal, finance]);

  async function save() {
    setBusy(true);
    try {
      await api.put("/admin/settings/legal", { data: l });
      await api.put("/admin/settings/finance", { data: { ...f, gst_on_service_fee_pct: +f.gst_on_service_fee_pct, shipping_flat: +f.shipping_flat, free_shipping_above: +f.free_shipping_above } });
      toast.success("Settings saved"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }
  const I = ({ k, obj, set, label }) => (
    <div><label className="overline text-olive">{label}</label>
      <input data-testid={`cfg-${k}`} className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm" value={obj[k] ?? ""} onChange={(e) => set({ ...obj, [k]: e.target.value })} /></div>
  );

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="mb-4 font-heading text-lg font-medium text-forest">VIPER OWL legal entity & finance config</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <I k="company_name" obj={l} set={setL} label="Company name" />
        <I k="gstin" obj={l} set={setL} label="GSTIN" />
        <I k="pan" obj={l} set={setL} label="PAN" />
        <I k="address" obj={l} set={setL} label="Address" />
        <I k="invoice_prefix" obj={l} set={setL} label="Invoice prefix" />
        <I k="financial_year" obj={l} set={setL} label="Financial year" />
        <I k="gst_on_service_fee_pct" obj={f} set={setF} label="GST on service fee %" />
        <I k="shipping_flat" obj={f} set={setF} label="Flat shipping ₹" />
        <I k="free_shipping_above" obj={f} set={setF} label="Free shipping above ₹" />
      </div>
      <Button data-testid="save-config" className="mt-4 bg-forest text-white hover:bg-forest/90" disabled={busy} onClick={save}>Save configuration</Button>
      <p className="mt-2 text-xs text-ink/50">A qualified CA must verify final invoice/tax configuration before production launch.</p>
    </div>
  );
}
